// The documents grid (spec/dashboard.md section 6.1): server-paged with the filter bar's rules, the sort and the search
// sent to the server; badges NEW / MOD / DEL / UND / HDN; a heir switcher; inline editors per type; the row and bulk
// menus (clone, export, replace by, copy to branch, change template, create override / A/B test, delete); the singleton default column; the
// document editor in the URL.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Document, Json, Parameter, TreeChange } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useContent } from '../content/ContentContext';
import { stageDeletion } from '../content/DocumentActions';
import { DocumentEditorHost } from '../content/DocumentEditor';
import { baseOf, cloneDocument, toServerFilter } from '../content/actions';
import { ChangeTemplateDialog } from '../content/dialogs/ChangeTemplateDialog';
import { CopyToBranchDialog } from '../content/dialogs/CopyToBranchDialog';
import { ImportExportDialog } from '../content/dialogs/ImportExportDialog';
import { ReplaceByDialog } from '../content/dialogs/ReplaceByDialog';
import { formatValue } from '../content/format';
import { parameterHelp } from '../content/help';
import { useCreateFor, type CreateForKind } from '../content/overrides/useCreateFor';
import { FieldWidget } from '../content/widgets/FieldWidget';
import { createDocumentDraft, heirsOf } from '../content/widgets/context';
import { useGame } from '../shell/GameContext';
import { routes } from '../shell/routes';
import { Button, IconButton } from '../ui/Button';
import { Badge, draftTone } from '../ui/Chip';
import { DataGrid, type Column, type SortState } from '../ui/DataGrid';
import { applyRules, FilterBar, useUrlFilter, type FilterField, type FilterRule } from '../ui/FilterBar';
import { Select, Switch } from '../ui/inputs';
import { useUrlParam } from '../ui/Modal';
import { Menu } from '../ui/Popover';
import { Download, MoreVertical, Pencil, Plus, Upload } from '../ui/icons';
import { useToasts } from '../ui/Toast';
import { useQuery } from '../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../workspace/react';
import { entityKey, isTempId, type Draft } from '../workspace/Workspace';

type Data = Record<string, Json>;

const GRID_EDITABLE = new Set(['int', 'long', 'float', 'bool', 'string', 'enum', 'date_time', 'duration', 'color', 'localized_string', 'document', 'asset', 'product', 'template_ref', 'profile_path', 'list', 'object']);

export function DocumentsPage({ template: fixedTemplate, title }: { template?: string; title?: string } = {}) {
  return (
    <DocumentEditorHost>
      <DocumentsGrid fixedTemplate={fixedTemplate} title={title} />
    </DocumentEditorHost>
  );
}

function DocumentsGrid({ fixedTemplate, title }: { fixedTemplate?: string; title?: string }) {
  const { org, game, can } = useGame();
  const { branch = '', template: routeTemplate = '' } = useParams();
  const template = fixedTemplate ?? routeTemplate;
  const { client } = useAuth();
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const version = useWorkspaceVersion(workspace);
  const catalog = useContent();
  const toasts = useToasts();
  const canWrite = can('content_write') && !workspace.isProtected;
  const templateDef = catalog.templates.get(template);
  const heirs = useMemo(() => heirsOf(catalog, template), [catalog, template]);
  const heirsParam = useUrlParam('heirs');
  const includeHeirs = heirsParam.value === '1';
  const params = useMemo(() => templateDef?.effectiveParameters ?? [], [templateDef]);
  const docParam = useUrlParam('doc');
  const dialogParam = useUrlParam('dialog');
  const sortParam = useUrlParam('sort');
  const sort = useMemo<SortState | null>(() => {
    if (!sortParam.value) return null;
    const [key, dir] = sortParam.value.split(':');
    return key ? { key, dir: dir === 'desc' ? 'desc' : 'asc' } : null;
  }, [sortParam.value]);

  const [search, setSearch] = useState('');
  const [rules, setRules] = useUrlFilter();
  const serverRules = useMemo(() => toServerFilter(rules, params), [rules, params]);
  const serverQuery = useMemo(
    () => ({ template, includeHeirs: includeHeirs ? 'true' : undefined, limit: 100, search: search || undefined, filter: serverRules.length > 0 ? JSON.stringify(serverRules) : undefined, sort: sort ? `${sort.key}:${sort.dir}` : undefined }),
    [template, includeHeirs, search, serverRules, sort],
  );

  // the paged list (server paging; the grid asks for more until the page on screen is full)
  const [items, setItems] = useState<Document[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const listKey = `${branch}|${JSON.stringify(serverQuery)}|${workspace.head ?? ''}`;
  const lastKey = useRef('');
  const load = useCallback(
    async (after: string | null, replace: boolean) => {
      setLoading(true);
      try {
        const page = await client.data(ops.listDocuments, { path: { org, game: game.slug, branch }, query: { ...serverQuery, cursor: after ?? undefined } });
        setItems((prev) => (replace ? page.items : [...prev, ...page.items]));
        setCursor(page.nextCursor);
        setTotal(page.total === null || page.total === undefined ? null : Number(page.total));
        setError(null);
        catalog.resolver.seed(page.items);
      } catch (e) {
        setError(e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), ''));
      } finally {
        setLoading(false);
      }
    },
    [client, org, game.slug, branch, serverQuery, catalog.resolver],
  );
  useEffect(() => {
    if (lastKey.current === listKey) return;
    lastKey.current = listKey;
    void load(null, true);
  }, [listKey, load]);

  // UND: documents changed since the branch's latest release (compare the release commit with the head)
  const releasesQ = useQuery(`game:${game.id}:releases`, () => client.data(ops.listReleases, { path: { org, game: game.slug }, query: { limit: 50 } }), { head: workspace.head });
  const latestRelease = useMemo(() => (releasesQ.data?.items ?? []).filter((r) => r.branchName === branch).sort((a, b) => Number(b.number) - Number(a.number))[0], [releasesQ.data, branch]);
  const undQ = useQuery(latestRelease && workspace.head && latestRelease.commit !== workspace.head ? `branch:${game.id}:${branch}:undeployed:${latestRelease.commit}:${workspace.head}` : null, () => client.data(ops.compareCommits, { path: { org, game: game.slug, branch }, query: { from: latestRelease!.commit, to: workspace.head! } }), {});
  const undeployed = useMemo(() => new Set(((undQ.data as { changes?: TreeChange[] } | undefined)?.changes ?? []).map((c) => c.id).filter((x): x is string => !!x)), [undQ.data]);

  // the drafts of this template family: new ones first, then the server rows with their drafts overlaid
  const family = useMemo(() => new Set([template, ...(includeHeirs ? heirs.map((h) => h.name) : [])]), [template, heirs, includeHeirs]);
  const rows = useMemo(() => {
    const drafts = workspace.all.filter((d) => d.kind === 'document' && family.has(d.template ?? ''));
    const newOnes = drafts.filter((d) => d.status === 'new').map((d) => d.current as unknown as Document);
    const overlaid = items.map((doc) => (workspace.getDraft(entityKey('document', doc.id))?.current as unknown as Document | undefined) ?? doc);
    return [...newOnes, ...overlaid];
  }, [items, workspace, family, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientRules = rules.filter((r) => r.field.startsWith('_'));
  const filtered = useMemo(() => applyRules(rows, clientRules, (doc, f) => (f === '_dirty' ? String(!!workspace.getDraft(entityKey('document', doc.id))) : (doc as unknown as Record<string, unknown>)[f])), [rows, clientRules, workspace, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const serverOf = (doc: Document): Document => items.find((d) => d.id === doc.id) ?? doc;
  const edit = useCallback(
    (doc: Document, path: string, value: unknown) => {
      const server = items.find((d) => d.id === doc.id) ?? doc;
      workspace.edit('document', doc.id, path, value, { base: baseOf(server), label: catalog.label(doc), template: doc.template });
    },
    [workspace, items, catalog],
  );
  const openDoc = (id: string) => docParam.open(id);
  const createDocument = () => {
    if (!templateDef) return;
    openDoc(createDocumentDraft(workspace, templateDef));
  };
  // section 6.1 Delete: staged at once (no dialog); the toast's Undo restores every document this action staged
  const deleteDocuments = async (docs: Document[]) => {
    stageDeletion(workspace, toasts, docs.map((doc) => ({ id: doc.id, label: catalog.label(doc), template: doc.template, base: baseOf(serverOf(doc)) })));
    if (docParam.value && docs.some((d) => d.id === docParam.value)) docParam.close();
  };
  const cloneDocs = (docs: Document[]) => {
    const ids = docs.map((d) => cloneDocument(workspace, catalog, d));
    toasts.success(`${ids.length} cop${ids.length === 1 ? 'y' : 'ies'} staged.`);
    if (ids.length === 1) openDoc(ids[0]!);
  };
  const setExport = (docs: Document[], value: boolean) => {
    for (const doc of docs) if (doc.export !== value) edit(doc, 'export', value);
  };
  const [dialog, setDialog] = useState<{ kind: 'replace'; doc: Document } | { kind: 'copy' | 'change'; docs: Document[] } | null>(null);
  const createFor = useCreateFor();
  const byIds = (ids: string[]) => ids.map((id) => rows.find((d) => d.id === id)).filter((d): d is Document => !!d);

  const rowDraft = (doc: Document): Draft | undefined => workspace.getDraft(entityKey('document', doc.id));
  const selectedDefault = templateDef?.kind === 'singleton' ? ((workspace.getDraft(entityKey('template-selected', template))?.current as { selected?: string | null } | undefined)?.selected ?? templateDef.selected ?? null) : null;
  const chooseDefault = (doc: Document) => {
    if (!templateDef) return;
    workspace.edit('template-selected', template, 'selected', doc.id, { base: { version: Number(templateDef.version), value: { selected: templateDef.selected ?? null } as unknown as Json }, label: `Default ${templateDef.displayName ?? template}` });
  };

  const columns: Column<Document>[] = [
    {
      key: 'id',
      header: 'ID',
      fixed: true,
      width: 170,
      sortable: true,
      render: (doc) => {
        const d = rowDraft(doc);
        return (
          <span className="row">
            <button type="button" className="btn btn-ghost btn-s" style={{ padding: 0 }} onClick={() => openDoc(doc.id)} aria-label={`Open ${catalog.label(doc)}`} title={catalog.label(doc)}>
              <code>{doc.unnyId ?? (isTempId(doc.id) ? doc.id : doc.id.slice(0, 8))}</code>
            </button>
            {d && <Badge tone={draftTone[d.status]}>{d.status === 'new' ? 'NEW' : d.status === 'deleted' ? 'DEL' : 'MOD'}</Badge>}
            {!d && undeployed.has(doc.id) && (
              <Badge tone="info" title={`Changed since release ${latestRelease?.number ?? ''}`}>
                UND
              </Badge>
            )}
            {!doc.export && (
              <Badge tone="warning" title="Not exported: absent from releases">
                HDN
              </Badge>
            )}
          </span>
        );
      },
      cellText: (doc) => doc.unnyId ?? doc.id,
    },
    ...(templateDef?.kind === 'singleton'
      ? [
          {
            key: 'default',
            header: 'Default',
            width: 70,
            render: (doc: Document) => <input type="radio" name={`default-${template}`} aria-label={`Default: ${catalog.label(doc)}`} checked={selectedDefault === doc.id} disabled={!canWrite} onChange={() => chooseDefault(doc)} title="The document the engine uses when no higher-priority one passes its condition (the default wins ties)" />,
          } as Column<Document>,
        ]
      : []),
    ...(includeHeirs ? [{ key: 'template', header: 'Template', sortable: true, render: (doc: Document) => <code className="small">{doc.template.split('.').pop()}</code>, cellText: (doc: Document) => doc.template } as Column<Document>] : []),
    ...params.map<Column<Document>>((p) => ({
      key: `data.${p.name}`,
      header: (
        <span title={parameterHelp(p, { template }, catalog.templates) ?? p.name}>
          {p.type === 'localized_string' ? '🌐 ' : ''}
          {p.displayName ?? p.name}
        </span>
      ),
      sortable: true,
      render: (doc) => formatValue(p, (doc.data as Data)?.[p.name], catalog.format),
      cellText: (doc) => formatValue(p, (doc.data as Data)?.[p.name], catalog.format),
      editor:
        canWrite && GRID_EDITABLE.has(p.type)
          ? (doc, done) =>
              rowDraft(doc)?.status === 'deleted' ? (
                formatValue(p, (doc.data as Data)?.[p.name], catalog.format)
              ) : (
                <FieldWidget
                  param={p}
                  value={(doc.data as Data)?.[p.name]}
                  owner={doc.id}
                  autoFocus
                  compact
                  onCommit={(v) => {
                    edit(doc, `data.${p.name}`, v);
                    done.commit(v);
                  }}
                  onCancel={done.cancel}
                />
              )
          : undefined,
      onPaste: canWrite && (p.type === 'string' || p.type === 'int' || p.type === 'long' || p.type === 'float') ? (doc, text) => edit(doc, `data.${p.name}`, p.type === 'string' || p.type === 'long' ? text : Number(text)) : undefined,
    })),
    { key: 'tags', header: 'Tags', render: (doc) => doc.tags.join(', '), cellText: (doc) => doc.tags.join(', ') },
    { key: 'version', header: 'Version', align: 'right', sortable: true, render: (doc) => (isTempId(doc.id) ? '—' : String(doc.version)) },
    {
      key: 'menu',
      header: '',
      width: 40,
      fixed: true,
      render: (doc) => (
        <RowMenu
          doc={doc}
          canWrite={canWrite}
          onOpen={() => openDoc(doc.id)}
          onClone={() => cloneDocs([doc])}
          onExport={() => setExport([doc], !doc.export)}
          onReplace={() => setDialog({ kind: 'replace', doc: serverOf(doc) })}
          onCopy={() => setDialog({ kind: 'copy', docs: [serverOf(doc)] })}
          onChange={() => setDialog({ kind: 'change', docs: [serverOf(doc)] })}
          onCreateFor={(kind) => createFor.open(kind, doc)}
          onDelete={() => void deleteDocuments([doc])}
        />
      ),
    },
  ];

  const filterFields: FilterField[] = [
    { key: 'id', label: 'Id', type: 'string', group: 'Document' },
    { key: 'unnyId', label: 'Legacy id', type: 'string', group: 'Document' },
    ...(includeHeirs ? [{ key: 'template', label: 'Template', type: 'enum', group: 'Document', options: [templateDef, ...heirs].filter((t): t is NonNullable<typeof t> => !!t).map((t) => ({ value: t.name, label: t.displayName ?? t.name })) } as FilterField] : []),
    { key: 'export', label: 'Exported', type: 'bool', group: 'Document' },
    { key: 'tags', label: 'Tag', type: 'tag', group: 'Document' },
    { key: 'version', label: 'Version', type: 'number', group: 'Document' },
    { key: '_dirty', label: 'Has unsaved changes', type: 'bool', group: 'Document' },
    ...params.filter((p) => filterTypeOf(p) !== null).map<FilterField>((p) => ({ key: `data.${p.name}`, label: p.displayName ?? p.name, type: filterTypeOf(p)!, group: 'Parameters', options: p.type === 'enum' && p.enum ? catalog.enums.get(p.enum)?.values.map((v) => ({ value: String(v.value), label: v.displayName ?? v.name })) : undefined })),
  ];

  return (
    <section>
      <div className="page-head">
        <h1>{title ?? templateDef?.displayName ?? template}</h1>
        <code className="muted">{template}</code>
        {templateDef && <Badge>{templateDef.kind}</Badge>}
        {templateDef && (
          <Button size="s" variant="ghost" icon={<Pencil aria-hidden="true" />} onClick={() => navigate(routes.template(org, game.slug, branch, template))}>
            Edit template
          </Button>
        )}
        <span className="spacer" />
        <Button size="s" icon={<Upload aria-hidden="true" />} onClick={() => dialogParam.open('import')} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected || !templateDef}>
          Import…
        </Button>
        <Button size="s" icon={<Download aria-hidden="true" />} onClick={() => dialogParam.open('export')} disabled={!templateDef}>
          Export…
        </Button>
        <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={createDocument} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected || !templateDef || templateDef.flags.includes('abstract')} title={workspace.isProtected ? 'Protected branch: changes land through merge requests.' : templateDef?.flags.includes('abstract') ? 'An abstract template has no documents.' : undefined}>
          New document
        </Button>
      </div>
      {heirs.length > 0 && (
        <div className="page-toolbar">
          <span className="muted small">Showing</span>
          <Select size="s" className="input-inline" value={template} onChange={(e) => navigate(routes.documents(org, game.slug, branch, e.target.value))} aria-label="Template or heir">
            <option value={template}>{templateDef?.displayName ?? template}</option>
            {heirs.map((h) => (
              <option key={h.name} value={h.name}>
                ↳ {h.displayName ?? h.name}
              </option>
            ))}
          </Select>
          <Switch label="Include heirs" checked={includeHeirs} onChange={(v) => heirsParam.set({ heirs: v ? '1' : null })} />
        </div>
      )}
      {!templateDef && !catalog.loading && (
        <p className="error" role="alert">
          No template named {template} on this branch.
        </p>
      )}
      <DataGrid
        id={`documents:${template}`}
        rows={filtered}
        rowKey={(doc) => doc.id}
        loading={loading}
        revision={version + catalog.version}
        caption={documentCount(filtered.length, cursor === null ? null : clientRules.length === 0 && total !== null ? total : undefined)}
        toolbar={<FilterBar id={`documents:${template}`} fields={filterFields} rules={rules} onChange={setRules as (r: FilterRule[]) => void} />}
        search={{ value: search, onSearch: setSearch, placeholder: 'Search documents, referenced names, enum values…' }}
        sort={sort}
        onSortChange={(s) => sortParam.set({ sort: s ? `${s.key}:${s.dir}` : null })}
        onRowOpen={(doc) => openDoc(doc.id)}
        rowClass={(doc) => {
          const d = rowDraft(doc);
          return d ? (d.status === 'deleted' ? 'deleted' : 'dirty') : '';
        }}
        selectable={canWrite}
        bulkActions={[
          { key: 'clone', label: 'Clone', onSelect: (ids) => cloneDocs(byIds(ids)) },
          { key: 'exportOn', label: 'Export: on', onSelect: (ids) => setExport(byIds(ids), true) },
          { key: 'exportOff', label: 'Export: off', onSelect: (ids) => setExport(byIds(ids), false) },
          { key: 'copy', label: 'Copy to branch…', onSelect: (ids) => setDialog({ kind: 'copy', docs: byIds(ids).map(serverOf) }) },
          { key: 'change', label: 'Change template…', onSelect: (ids) => setDialog({ kind: 'change', docs: byIds(ids).map(serverOf) }) },
          { key: 'delete', label: 'Delete', danger: true, onSelect: (ids) => void deleteDocuments(byIds(ids)) },
        ]}
        hasMore={cursor !== null}
        onLoadMore={() => void load(cursor, false)}
        total={clientRules.length === 0 ? total : null}
        pageResetKey={[template, includeHeirs, JSON.stringify(rules)].join('|')}
        emptyState={error ? `${error.message} (request ${error.requestId})` : rules.length > 0 || search ? `No ${templateDef?.displayName ?? template} document matches.` : `No ${templateDef?.displayName ?? template} documents yet.`}
        columns={columns}
      />
      {dialog?.kind === 'replace' && <ReplaceByDialog doc={dialog.doc} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'copy' && <CopyToBranchDialog docs={dialog.docs} onClose={() => setDialog(null)} />}
      <ImportExportDialog layout="template" template={template} />
      {dialog?.kind === 'change' && <ChangeTemplateDialog docs={dialog.docs} onClose={() => setDialog(null)} onDone={() => void load(null, true)} />}
      {createFor.dialog}
    </section>
  );
}

/**
 * The grid's caption: every document of the list (the pager shows which are on screen). `more` is the server's total
 * while pages are still to load (`null`: all loaded; `undefined`: unknown — a client-side rule filters the loaded ones).
 */
function documentCount(loaded: number, more: number | null | undefined): string {
  const n = more === null ? loaded : Math.max(more ?? 0, loaded);
  return `${n}${more === undefined ? '+' : ''} document${n === 1 ? '' : 's'}`;
}

function filterTypeOf(p: Parameter): FilterField['type'] | null {
  switch (p.type) {
    case 'int':
    case 'long':
    case 'float':
    case 'duration':
      return 'number';
    case 'date_time':
      return 'date';
    case 'bool':
      return 'bool';
    case 'string':
    case 'document':
    case 'localized_string':
    case 'asset':
    case 'template_ref':
    case 'profile_path':
      return 'string';
    case 'enum':
      return p.enum ? 'enum' : 'number';
    default:
      return null;
  }
}

function RowMenu({ doc, canWrite, onOpen, onClone, onExport, onReplace, onCopy, onChange, onCreateFor, onDelete }: { doc: Document; canWrite: boolean; onOpen: () => void; onClone: () => void; onExport: () => void; onReplace: () => void; onCopy: () => void; onChange: () => void; onCreateFor: (kind: CreateForKind) => void; onDelete: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const temp = isTempId(doc.id);
  return (
    <>
      <IconButton
        label={`Actions for ${doc.unnyId ?? doc.id.slice(0, 8)}`}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        aria-haspopup="menu"
      >
        <MoreVertical aria-hidden="true" />
      </IconButton>
      <Menu
        anchor={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        label="Document actions"
        items={[
          { key: 'open', label: 'Open', onSelect: onOpen },
          { key: 'clone', label: 'Clone', onSelect: onClone, disabled: !canWrite },
          { key: 'export', label: doc.export ? 'Export: on → off' : 'Export: off → on', onSelect: onExport, disabled: !canWrite },
          { key: 'replace', label: 'Replace by…', onSelect: onReplace, disabled: !canWrite || temp },
          { key: 'copy', label: 'Copy to branch…', onSelect: onCopy, disabled: temp },
          { key: 'change', label: 'Change template…', onSelect: onChange, disabled: !canWrite || temp },
          { key: 's1', label: '', separator: true },
          { key: 'override', label: 'Create override…', onSelect: () => onCreateFor('override'), disabled: !canWrite },
          { key: 'abtest', label: 'Create A/B test…', onSelect: () => onCreateFor('abtest'), disabled: !canWrite },
          { key: 's2', label: '', separator: true },
          { key: 'delete', label: 'Delete', onSelect: onDelete, danger: true, disabled: !canWrite },
        ]}
      />
    </>
  );
}
